import { supabase } from '../supabase/config';

// ─── Push subscription helpers ────────────────────────────────────────────────
// Web Push (VAPID) removed — Android app uses FCM via @capacitor/push-notifications.
// FCM registration is handled in src/native/push.js (Phase 2).

export const registerPushSubscription = async () => {
  if (window._registerFcmPush) {
    try { await window._registerFcmPush(); } catch (e) { console.error('FCM register error:', e); }
  }
};

// Reuse the in-app popup for foreground FCM pushes (notification system tray
// doesn't fire on Android when the app is in foreground).
if (typeof window !== 'undefined' && !window._fcmForegroundBound) {
  window._fcmForegroundBound = true;
  window.addEventListener('fcmForegroundPush', (e) => {
    showNotificationPopup(e.detail);
  });
}

// ─── In-app notification popup ────────────────────────────────────────────────

const getNotificationMeta = (type) => {
  switch (type) {
    case 'join_request':      return { emoji: '👋', bg: '#fff3cd' };
    case 'request_accepted':  return { emoji: '✅', bg: '#e8f5e9' };
    case 'user_joined':       return { emoji: '🙋', bg: '#e3f2fd' };
    case 'user_left':         return { emoji: '👋', bg: '#fff3e0' };
    case 'removed_from_event':return { emoji: '❌', bg: '#fef7f7' };
    case 'event_deleted':     return { emoji: '🗑️', bg: '#fef7f7' };
    case 'new_message':       return { emoji: '💬', bg: '#f3e5f5' };
    default:                  return { emoji: '🔔', bg: '#f8f9fa' };
  }
};

const showNotificationPopup = (notification) => {
  if (!notification) return;
  const existing = document.getElementById('notif-popup');
  if (existing) existing.remove();

  const { emoji, bg } = getNotificationMeta(notification.type);
  const title = notification.title || 'New Notification';
  const body = notification.body || notification.message || '';

  const popup = document.createElement('div');
  popup.id = 'notif-popup';
  popup.style.cssText = `
    position: fixed;
    top: 79px;
    left: 50%;
    transform: translateX(-50%) translateY(-20px);
    width: calc(100vw - 16px);
    max-width: 360px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.1);
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    z-index: 30000;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.25s ease, transform 0.25s ease;
    font-family: 'Outfit', sans-serif;
    border: 1px solid #f0f0f0;
  `;
  popup.innerHTML = `
    <div style="width:36px;height:36px;border-radius:50%;background:${bg};display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:16px;">${emoji}</div>
    <div style="flex:1;min-width:0;">
      <div style="font-size:13px;font-weight:600;color:#202124;margin-bottom:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${title}</div>
      <div style="font-size:12px;color:#5f6368;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${body}</div>
    </div>
    <div style="font-size:11px;color:#9aa0a6;flex-shrink:0;">Just now</div>
  `;
  popup.addEventListener('click', () => {
    popup.remove();
    if (window._setNotificationsOpen) window._setNotificationsOpen(true);
  });
  document.body.appendChild(popup);
  requestAnimationFrame(() => requestAnimationFrame(() => {
    popup.style.opacity = '1';
    popup.style.transform = 'translateX(-50%) translateY(0)';
  }));
  setTimeout(() => {
    popup.style.opacity = '0';
    popup.style.transform = 'translateX(-50%) translateY(-20px)';
    setTimeout(() => popup.remove(), 250);
  }, 4000);
};

// ─── Notification CRUD ────────────────────────────────────────────────────────

export const markNotificationAsRead = async (notificationId) => {
  const supabaseClient = window.supabase || supabase;
  await supabaseClient.from('notifications').update({ is_read: true }).eq('id', notificationId);
  if (window._setNotificationCount) window._setNotificationCount(prev => Math.max(0, prev - 1));
  if (window.loadNotifications) window.loadNotifications();
};

export const markAllNotificationsAsRead = async () => {
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  if (!currentUserId) return;
  const supabaseClient = window.supabase || supabase;
  try {
    await supabaseClient.from('notifications').update({ is_read: true }).eq('user_id', currentUserId).eq('is_read', false);
    if (window.loadNotifications) window.loadNotifications();
    if (window.updateNotificationCount) window.updateNotificationCount();
  } catch (error) {
    console.error('Error marking all as read:', error);
  }
};

export const loadNotifications = async () => {
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  if (!currentUserId) return;
  const supabaseClient = window.supabase || supabase;
  const { data, error } = await supabaseClient
    .from('notifications').select('*').eq('user_id', currentUserId)
    .order('created_at', { ascending: false }).limit(50);
  if (!error && data) {
    window.dispatchEvent(new CustomEvent('notificationsLoaded', { detail: { notifications: data } }));
  }
};

export const updateNotificationCount = async () => {
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  if (!currentUserId) return;
  const supabaseClient = window.supabase || supabase;
  const { data } = await supabaseClient.from('notifications').select('id').eq('user_id', currentUserId).eq('is_read', false);
  if (window._setNotificationCount) window._setNotificationCount(data?.length || 0);
};

export const setupNotificationSubscription = () => {
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  if (!currentUserId) return;
  const supabaseClient = window.supabase || supabase;
  if (window.notificationSubscription) supabaseClient.removeChannel(window.notificationSubscription);
  window.notificationSubscription = supabaseClient
    .channel(`notifications-${currentUserId}`)
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${currentUserId}` },
      async (payload) => {
        if (window._setNotificationCount) window._setNotificationCount(prev => Math.min(prev + 1, 99));
        if (window.loadNotifications) window.loadNotifications();
        showNotificationPopup(payload.new);
      }
    ).subscribe();
};

export const getUserOnlineStatus = async (userId) => {
  try {
    const supabaseClient = window.supabase || supabase;
    const { data, error } = await supabaseClient.from('users').select('online_status, last_seen').eq('id', userId).single();
    if (error) throw error;
    return data.online_status === 'online' && data.last_seen && (new Date() - new Date(data.last_seen)) < 120000;
  } catch { return false; }
};

window.markNotificationAsRead = markNotificationAsRead;
window.loadNotifications = loadNotifications;
window.updateNotificationCount = updateNotificationCount;
window.setupNotificationSubscription = setupNotificationSubscription;
window.markAllNotificationsAsRead = markAllNotificationsAsRead;
window.getUserOnlineStatus = getUserOnlineStatus;
window.registerPushSubscription = registerPushSubscription;